import React, { useState, useEffect } from 'react';

const SCIENCE_TOUR = [
  {
    view: 'orbital',
    title: 'Start here — the Higgs field',
    body: 'The golden H⁰ at the center is the Higgs boson — a ripple in the Higgs field. Every particle orbits it at a distance proportional to how strongly it couples to the field. Inner orbit = stronger coupling = greater mass. The dashed outer rings are particles that don\'t couple at all — the photon and gluon — which is why they remain massless.',
    action: 'Watch the particles orbit. Notice how the top quark (t) sits closest — it has the strongest Higgs coupling of any fermion.',
    highlight: null,
  },
  {
    view: 'orbital',
    title: 'Toggle antimatter',
    body: 'Every particle has an antiparticle — identical in mass but opposite in charge. Click "Show antimatter" to reveal them orbiting just outside their matter counterparts on dashed rings. When matter meets antimatter, they annihilate — converting entirely to energy.',
    action: 'Click the "Show antimatter" button above the orbital map. Notice that the photon, gluon, and Higgs have no distinct antiparticle — they are self-conjugate.',
    highlight: 'antimatter',
  },
  {
    view: 'proton',
    title: 'The proton surprise',
    body: 'Click "Assemble proton" and watch the mass counter. Three quarks (uud) have a combined Higgs mass of only 9.4 MeV. But the proton weighs 938 MeV — over 100 times more. That extra 99% comes from the kinetic energy of quarks moving at near-light speed, and the gluon binding energy that holds them together.',
    action: 'Press "Assemble proton" and watch the mass counter climb from 9.4 MeV to 938 MeV.',
    highlight: null,
  },
  {
    view: 'whatami',
    title: 'What are YOU made of?',
    body: 'Enter your weight. The app calculates your atoms, quarks, and gluons — and shows you that roughly 99% of your physical mass comes not from the Higgs field but from QCD binding energy inside your protons and neutrons.',
    action: 'Enter your weight and hit Calculate. The result will surprise you.',
    highlight: null,
  },
  {
    view: 'decay',
    title: 'The Higgs paradox — light from mass',
    body: 'The Higgs boson decays into many things. Most make sense — it decays to heavy particles it\'s strongly coupled to. But click the γγ branch. The Higgs decays to two photons — which have zero mass and zero Higgs coupling. How? Through virtual loops of top quarks and W bosons acting as intermediaries.',
    action: 'Click the γγ branch (0.23%). Read the explanation of why massless particles emerge from the mass-giving field.',
    highlight: null,
  },
  {
    view: 'timeline',
    title: 'Drag back to the beginning',
    body: 'Drag the slider all the way left. At 10⁻¹² seconds after the Big Bang, the Higgs field switches off — W and Z bosons become massless, electromagnetism and the weak force merge into one unified electroweak force. Keep going to the Planck era where space and time lose meaning entirely.',
    action: 'Drag the slider from "Now" all the way to the Planck era. Watch each era transition.',
    highlight: null,
  },
  {
    view: 'cracks',
    title: 'The 95% problem',
    body: 'Everything we\'ve explored describes about 5% of the universe\'s total energy content. Dark matter (27%) and dark energy (68%) are completely outside the Standard Model. Open each crack to see where physics currently stops.',
    action: 'Click each crack open. The matter/antimatter asymmetry section explains why you exist at all.',
    highlight: null,
  },
  {
    view: 'feynman',
    title: 'Build an interaction',
    body: 'The Feynman diagram builder lets you construct particle interactions. Try the QED vertex — add an electron, a positron, and a photon. The app checks conservation laws in real time. Try building an invalid combination and watch the conservation law checker flag it.',
    action: 'Click "e⁻ + e⁺ + γ" from the known vertices list to load the fundamental QED vertex.',
    highlight: null,
  },
];

const RUACH_TOUR = [
  {
    view: 'orbital',
    title: 'The Breath that gives form — רוּחַ אֱלֹהִים',
    body: '"The Spirit of God was hovering over the waters." — Genesis 1:2. The Ruach Elohim at the center is the field that gives weight and form to all created things. Those closest to it receive the greatest measure of the breath. Those on the outer dashed rings — pure light, pure bond — receive none, and remain formless, infinite.',
    action: 'Watch the particles orbit the Ruach. Those closest are the heaviest. The photon (אוֹר) orbits on a dashed ring — untouched.',
    highlight: null,
  },
  {
    view: 'orbital',
    title: 'Show the mirrors — מַרְאוֹת',
    body: 'Every formed thing has a mirror — an antiparticle, identical in weight but opposite in charge. Toggle the mirrors. At creation, matter and antimatter were made in equal amounts. Something caused a tiny surplus of matter — 1 in a billion — and that surplus became everything. "You are precious and honored in my sight." — Isaiah 43:4',
    action: 'Click "Show mirrors — מַרְאוֹת". Notice that the Ruach Elohim (Higgs) has no mirror — it is self-complete. "I am the LORD, and there is no other." — Isaiah 45:6',
    highlight: 'antimatter',
  },
  {
    view: 'proton',
    title: 'The hidden binding — Colossians 1:17',
    body: '"In him all things hold together." Three utterances — up, up, down — bound by the gluon\'s confinement. The Ruach gives the quarks their tiny measure of weight. But almost all the proton\'s weight comes from the binding itself. You are not primarily Ruach-weight. You are primarily the energy of holding together.',
    action: 'Press "Bind the utterances" and watch 9.4 MeV become 938 MeV through the hidden bond.',
    highlight: null,
  },
  {
    view: 'whatami',
    title: 'How much of you is Ruach-given weight?',
    body: '"I praise you because I am fearfully and wonderfully made." — Psalm 139:14. Enter your weight. Discover that only a tiny fraction of your physical substance comes directly from the Ruach field. The rest — 99% — is the energy of the hidden bond holding your quarks together inside every proton and neutron in your body.',
    action: 'Enter your weight and press Reveal.',
    highlight: null,
  },
  {
    view: 'decay',
    title: 'The Breath returns to light — Genesis 1:3',
    body: '"And God said, Let there be light — וַיְהִי אוֹר." The most profound channel in the entire model: the Ruach Elohim dissolves into pure light — אוֹר — the first creation. The massless photon, which does not interact with the Ruach field at all, emerges FROM the Ruach through the mediation of formed things (top quarks and W bosons). The first word spoken is the last word in every decay.',
    action: 'Click the γγ branch (0.23%). Read the Ruach interpretation. The Breath returns to light.',
    highlight: null,
  },
  {
    view: 'timeline',
    title: 'Back to Tohu va-Bohu — תֹהוּ וָבֹהוּ',
    body: '"The earth was formless and empty — תֹהוּ וָבֹהוּ — and the Spirit of God was hovering over the waters." — Genesis 1:2. Drag the slider backward. Watch the Ruach field switch off — particles lose their weight, boundaries dissolve, forces unify. At the Planck era, even space and time lose their form. The void before the first utterance.',
    action: 'Drag the slider all the way to Tohu va-Bohu. Read the scripture at each era.',
    highlight: null,
  },
  {
    view: 'color',
    title: 'Three become one — the Shema in matter',
    body: '"Hear, O Israel: The LORD our God, the LORD is one." — Deuteronomy 6:4. Every proton requires exactly three quarks, each carrying a different color charge — red, green, blue. Together they form white: colorless, unified, complete. Three distinct things that must become one. "For where two or three gather in my name, there am I with them." — Matthew 18:20',
    action: 'Select "Proton" and "Invalid (two red)". See why uniformity is forbidden and difference is required.',
    highlight: null,
  },
  {
    view: 'cracks',
    title: 'Where knowing ends — אֵין סוֹף',
    body: '"For now we see only a reflection as in a mirror; then we shall see face to face." — 1 Corinthians 13:12. The Standard Model describes 5% of creation with extraordinary precision. Dark matter, dark energy, gravity — 95% of what exists — lies beyond it. These are not failures. They are the places where science reaches the boundary and points toward something larger.',
    action: 'Open the "Matter/antimatter asymmetry" crack. Read why your very existence requires physics beyond our current model.',
    highlight: null,
  },
  {
    view: 'scale',
    title: 'From creation\'s expanse to the void',
    body: '"He stretches out the heavens like a canopy." — Isaiah 40:22. Drag from the observable universe — everything YHWH stretched out — all the way down to the Planck length where space and time lose meaning. Scripture accompanies every scale. At each step you move through layers of creation, each one nested inside the next.',
    action: 'Drag from the observable universe all the way to Tohu va-Bohu at 10⁻³⁵ meters.',
    highlight: null,
  },
];

export default function GuidedTour({ mode, onNavigate, onClose }) {
  const isRuach = mode === 'ruach';
  const tour = isRuach ? RUACH_TOUR : SCIENCE_TOUR;
  const [step, setStep] = useState(0);
  const accentColor = isRuach ? '#ffb060' : '#f0c040';
  const current = tour[step];

  useEffect(() => {
    if (current?.view) onNavigate(current.view);
  }, [step, current]);

  const next = () => { if (step < tour.length - 1) setStep(s => s + 1); else onClose(); };
  const prev = () => { if (step > 0) setStep(s => s - 1); };

  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)',
      width: 'min(520px, calc(100vw - 3rem))',
      background: isRuach ? 'rgba(14,10,4,0.97)' : 'rgba(8,8,16,0.97)',
      border: `1px solid ${accentColor}40`,
      borderRadius: '16px', zIndex: 150,
      backdropFilter: 'blur(20px)',
      animation: 'tourIn 0.25s ease-out',
      boxShadow: `0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px ${accentColor}20`,
    }}>
      <style>{`@keyframes tourIn{from{opacity:0;transform:translateX(-50%) translateY(20px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>

      {/* Progress bar */}
      <div style={{ height: '2px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px 2px 0 0', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((step + 1) / tour.length) * 100}%`, background: accentColor, transition: 'width 0.3s ease', borderRadius: '2px' }} />
      </div>

      <div style={{ padding: '1.25rem 1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div>
            <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '0.3rem' }}>
              {isRuach ? 'Ruach path' : 'Science tour'} · {step + 1} of {tour.length}
            </div>
            <h3 style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1rem', color: '#fff', margin: 0 }}>
              {current.title}
            </h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', borderRadius: '6px', width: '28px', height: '28px', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: '1rem' }}>×</button>
        </div>

        {/* Body */}
        <p style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.75, margin: '0 0 0.875rem' }}>
          {current.body}
        </p>

        {/* Action hint */}
        <div style={{ padding: '0.6rem 0.875rem', borderRadius: '7px', background: `${accentColor}0d`, border: `1px solid ${accentColor}25`, marginBottom: '1rem' }}>
          <span style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: accentColor }}>→ </span>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{current.action}</span>
        </div>

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button onClick={prev} disabled={step === 0} style={{
            padding: '0.5rem 1rem', borderRadius: '7px', cursor: step === 0 ? 'default' : 'pointer',
            background: 'none', border: '1px solid rgba(255,255,255,0.12)',
            color: step === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.55)',
            fontFamily: 'Space Mono', fontSize: '0.72rem',
          }}>← Back</button>

          {/* Step dots */}
          <div style={{ display: 'flex', gap: '5px' }}>
            {tour.map((_, i) => (
              <div key={i} onClick={() => setStep(i)} style={{
                width: i === step ? '18px' : '6px', height: '6px', borderRadius: '3px',
                background: i === step ? accentColor : 'rgba(255,255,255,0.2)',
                cursor: 'pointer', transition: 'all 0.2s',
              }} />
            ))}
          </div>

          <button onClick={next} style={{
            padding: '0.5rem 1rem', borderRadius: '7px', cursor: 'pointer',
            background: `${accentColor}20`, border: `1px solid ${accentColor}50`,
            color: accentColor, fontFamily: 'Space Mono', fontSize: '0.72rem', fontWeight: 700,
          }}>
            {step === tour.length - 1 ? 'Finish' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
}
